import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, MapIcon, ExternalLink, Ruler, Filter } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { TrailCompletion } from "@/hooks/useEnhancedUserMetrics";
import { Slider } from "@/components/ui/slider";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { loadModules, setDefaultOptions } from "esri-loader";
import { ROUTES } from "@/lib/routes";
import { useUserLocation } from "@/hooks/useUserLocation";
import { formatDistanceToNow } from "date-fns";

interface TrailCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trails: TrailCompletion[];
}

const ITEMS_PER_PAGE = 20;

export function TrailCompletionDialog({
  open,
  onOpenChange,
  trails,
}: TrailCompletionDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [minLength, setMinLength] = useState(2);
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [selectedTrailId, setSelectedTrailId] = useState<string | null>(null);
  const [completedOnly, setCompletedOnly] = useState(true);
  const [onlyOnMap, setOnlyOnMap] = useState(false);
  const [mapExtent, setMapExtent] = useState<any>(null);
  const [hoverInfo, setHoverInfo] = useState<{
    x: number;
    y: number;
    trailName: string;
  } | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapView, setMapView] = useState<any>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const { location: userLocation } = useUserLocation();
  const segmentsLayerRef = useRef<any>(null);
  const featureEffectModuleRef = useRef<any>(null);
  const trailExtentsRef = useRef<Map<string, any>>(new Map());

  const filteredTrails = trails
    .filter((trail) => {
      // Text search filter
      if (!trail.trail_name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Minimum length filter
      if (trail.total_length_miles < minLength) {
        return false;
      }
      // Completed only filter (80%+ completion)
      if (completedOnly && trail.completion_percentage < 80) {
        return false;
      }
      // Only on map filter - check if trail extent intersects current map extent
      if (onlyOnMap && mapExtent) {
        const trailExtent = trailExtentsRef.current.get(trail.trail_id);
        if (trailExtent && !mapExtent.intersects(trailExtent)) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      // Primary: last_completed_at descending (most recent first)
      const dateA = a.last_completed_at ? new Date(a.last_completed_at).getTime() : 0;
      const dateB = b.last_completed_at ? new Date(b.last_completed_at).getTime() : 0;
      if (dateB !== dateA) return dateB - dateA;
      // Secondary: total_length_miles descending (longest first)
      return b.total_length_miles - a.total_length_miles;
    });

  const displayedTrails = filteredTrails.slice(0, displayCount);
  const hasMore = displayCount < filteredTrails.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + ITEMS_PER_PAGE);
  };

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [searchQuery, minLength, completedOnly, onlyOnMap, mapExtent]);

  const handleSeeDetails = (e: React.MouseEvent, trailId: string) => {
    e.stopPropagation();
    onOpenChange(false);
    navigate(ROUTES.trail(trailId));
  };

  // CIM Symbol for trail segments (matching TrailMap width)
  const createCIMSymbol = (color: string) => ({
    type: "cim",
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMLineSymbol",
        symbolLayers: [
          {
            type: "CIMSolidStroke",
            enable: true,
            capStyle: "Round",
            joinStyle: "Round",
            width: 2,
            color: color === "green" ? [34, 197, 94, 255] : [156, 163, 175, 255],
          },
        ],
      },
    },
  });

  // Initialize map when dialog opens
  useEffect(() => {
    console.log("[TrailCompletionDialog] useEffect triggered - open:", open, "mapRef.current:", !!mapRef.current, "user:", !!user);
    
    if (!open) {
      console.log("[TrailCompletionDialog] Skipping - dialog not open");
      return;
    }
    if (!user) {
      console.log("[TrailCompletionDialog] Skipping - user is null");
      return;
    }

    let view: any = null;
    let timeoutId: NodeJS.Timeout;

    // Small delay to let the dialog DOM mount before accessing mapRef
    timeoutId = setTimeout(() => {
      if (!mapRef.current) {
        console.log("[TrailCompletionDialog] After timeout - mapRef.current still null");
        return;
      }
      console.log("[TrailCompletionDialog] After timeout - mapRef.current is ready, calling initMap");
      initMap();
    }, 100);

    const initMap = async () => {
      setIsMapLoading(true);
      console.log("[TrailCompletionDialog] initMap started");

      try {
        setDefaultOptions({ version: "4.33" });

        const [
          Map,
          MapView,
          GeoJSONLayer,
          Basemap,
          VectorTileLayer,
          BasemapToggle,
          Zoom,
          FeatureEffect,
          FeatureFilter,
        ] = await loadModules([
          "esri/Map",
          "esri/views/MapView",
          "esri/layers/GeoJSONLayer",
          "esri/Basemap",
          "esri/layers/VectorTileLayer",
          "esri/widgets/BasemapToggle",
          "esri/widgets/Zoom",
          "esri/layers/support/FeatureEffect",
          "esri/layers/support/FeatureFilter",
        ], { css: true });

        featureEffectModuleRef.current = { FeatureEffect, FeatureFilter };

        // Fetch user segments to determine completion
        const { data: userSegments, error: segmentsError } = await supabase
          .from("user_segments")
          .select("trail_segment_id")
          .eq("user_id", user.id);
        
        if (segmentsError) {
          console.error("[TrailCompletionDialog] Error fetching user segments:", segmentsError);
        }

        const completedSegmentIds = new Set(
          (userSegments || []).map((s) => s.trail_segment_id)
        );

        // Use Outdoors basemap like TrailMap
        const outdoorsLayer = new VectorTileLayer({
          portalItem: { id: "ef4bc627ccab4a80990ed476d53020d6" },
        });

        const map = new Map({
          layers: [outdoorsLayer],
        });

        // Determine map center - use user's home location or default to SoCal
        const mapCenter = userLocation?.home_longitude && userLocation?.home_latitude
          ? [userLocation.home_longitude, userLocation.home_latitude]
          : [-117.1893, 34.2515];

        view = new MapView({
          container: mapRef.current,
          map: map,
          center: mapCenter,
          zoom: 9,
          ui: {
            components: []
          }
        });

        await view.when();

        // Add trail segments layer with CIM symbology
        const geojsonUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trail-segments-geojson`;
        
        const segmentsLayer = new GeoJSONLayer({
          url: geojsonUrl,
          outFields: ["id", "trail_id", "trail_name", "difficulty", "length_miles"],
          renderer: {
            type: "unique-value",
            field: "id",
            uniqueValueInfos: Array.from(completedSegmentIds).map(
              (segmentId) => ({
                value: segmentId,
                symbol: createCIMSymbol("green"),
              })
            ),
            defaultSymbol: createCIMSymbol("gray"),
          } as any,
          popupEnabled: false,
        });

        segmentsLayerRef.current = segmentsLayer;
        map.add(segmentsLayer);

        // Add hover tooltip handler
        view.on("pointer-move", async (event: any) => {
          const response = await view.hitTest(event);
          const result = response.results.find(
            (r: any) => r.graphic?.layer === segmentsLayer
          );

          if (result?.graphic?.attributes) {
            const trailName = result.graphic.attributes.trail_name || "Unknown Trail";
            setHoverInfo({
              x: event.x,
              y: event.y,
              trailName,
            });
          } else {
            setHoverInfo(null);
          }
        });

        // Clear tooltip when pointer leaves
        view.on("pointer-leave", () => {
          setHoverInfo(null);
        });

        // Add zoom widget
        const zoomWidget = new Zoom({ view });
        view.ui.add(zoomWidget, "bottom-right");

        // Add satellite basemap toggle
        const satelliteBasemap = new Basemap({
          portalItem: { id: "10df2279f9684e4a9f6a7f08febac2a9" }
        });

        const basemapToggle = new BasemapToggle({
          view: view,
          nextBasemap: satelliteBasemap,
        });

        view.ui.add(basemapToggle, "bottom-right");

        // Track map extent for "Only on Map" filter
        view.watch("extent", (newExtent: any) => {
          setMapExtent(newExtent);
        });

        // Pre-fetch trail extents for intersection checking
        segmentsLayer.when(async () => {
          for (const trail of trails) {
            try {
              const query = segmentsLayer.createQuery();
              query.where = `trail_id = '${trail.trail_id}'`;
              const result = await segmentsLayer.queryExtent(query);
              if (result.extent) {
                trailExtentsRef.current.set(trail.trail_id, result.extent);
              }
            } catch (e) {
              // Ignore errors for individual trails
            }
          }
        });

        setMapView(view);
        setIsMapLoading(false);
      } catch (error) {
        console.error("[TrailCompletionDialog] Error initializing map:", error);
        setIsMapLoading(false);
      }
    };

    return () => {
      clearTimeout(timeoutId);
      if (view) {
        view.destroy();
      }
      segmentsLayerRef.current = null;
    };
  }, [open, user, userLocation]);

  // Apply feature effect when trail is selected
  useEffect(() => {
    if (!segmentsLayerRef.current || !featureEffectModuleRef.current || !selectedTrailId) {
      // Clear effect if no selection
      if (segmentsLayerRef.current) {
        segmentsLayerRef.current.featureEffect = null;
      }
      return;
    }

    const { FeatureEffect, FeatureFilter } = featureEffectModuleRef.current;

    segmentsLayerRef.current.featureEffect = new FeatureEffect({
      filter: new FeatureFilter({
        where: `trail_id = '${selectedTrailId}'`,
      }),
      includedEffect: "drop-shadow(3px, 3px, 4px, rgba(0, 0, 0, 0.5))",
      excludedEffect: "opacity(0.4)",
    });
  }, [selectedTrailId]);

  // Zoom to trail when clicked in list (toggle selection)
  const handleTrailClickWithZoom = async (trailId: string) => {
    // Toggle selection - if already selected, unselect
    if (selectedTrailId === trailId) {
      setSelectedTrailId(null);
      return;
    }
    
    setSelectedTrailId(trailId);

    if (mapView && segmentsLayerRef.current) {
      try {
        // Query layer extent for this trail
        const query = segmentsLayerRef.current.createQuery();
        query.where = `trail_id = '${trailId}'`;
        query.returnGeometry = true;

        const result = await segmentsLayerRef.current.queryExtent(query);
        
        if (result.extent) {
          await mapView.goTo(result.extent.expand(1.5), {
            duration: 500,
          });
        }
      } catch (error) {
        console.error("Error zooming to trail:", error);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            Completed Trails
            <Badge variant="secondary" className="text-xs">
              {trails.length}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Trails where you've completed at least 80% of segments
          </DialogDescription>
        </DialogHeader>

        {/* Filters: Search + Min Length Slider + Toggles */}
        <div className="px-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search trails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-3 min-w-[200px]">
              <Ruler className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <Slider
                value={[minLength]}
                onValueChange={(value) => setMinLength(value[0])}
                min={0}
                max={25}
                step={1}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap w-16">
                ≥{minLength} mi
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="completed-only"
                checked={completedOnly}
                onCheckedChange={setCompletedOnly}
              />
              <Label htmlFor="completed-only" className="text-sm cursor-pointer">
                Completed Only
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="only-on-map"
                checked={onlyOnMap}
                onCheckedChange={setOnlyOnMap}
              />
              <Label htmlFor="only-on-map" className="text-sm cursor-pointer">
                Only on Map
              </Label>
            </div>
          </div>
        </div>

        {/* Split Layout: Map + List */}
        <div className="flex flex-col md:flex-row gap-4 px-6 pb-6 h-[600px]">
          {/* Map Section - 60% on desktop */}
          <div className="flex-1 md:w-[60%] border rounded-lg overflow-hidden relative h-[300px] md:h-full">
            <div ref={mapRef} className="w-full h-full" />
            {isMapLoading && (
              <div className="absolute inset-0 bg-muted/50 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <MapIcon className="h-12 w-12 mx-auto mb-2 opacity-50 animate-pulse" />
                  <p className="text-sm">Loading map...</p>
                </div>
              </div>
            )}
            {/* Hover Tooltip */}
            {hoverInfo && (
              <div
                className="absolute pointer-events-none bg-background/95 backdrop-blur-sm px-3 py-2 rounded-lg border shadow-lg text-sm z-10"
                style={{
                  left: hoverInfo.x + 12,
                  top: hoverInfo.y + 12,
                }}
              >
                {hoverInfo.trailName}
              </div>
            )}
            <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg border text-xs shadow-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-0.5 bg-emerald-500" />
                <span>Completed Segments</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-gray-400" />
                <span>Incomplete Segments</span>
              </div>
            </div>
          </div>

          {/* Trail List Section - 40% on desktop */}
          <div className="flex-1 md:w-[40%] flex flex-col">
            <ScrollArea className="flex-1 pr-4">
              {filteredTrails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery
                    ? "No trails found matching your search"
                    : "No completed trails yet"}
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedTrails.map((trail) => (
                    <div
                      key={trail.trail_id}
                      onClick={() => handleTrailClickWithZoom(trail.trail_id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all animate-fade-in ${
                        selectedTrailId === trail.trail_id
                          ? "ring-2 ring-primary bg-accent"
                          : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm mb-1">
                            {trail.trail_name}
                          </h4>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span>
                              {trail.completed_segments}/{trail.total_segments}{" "}
                              segments
                            </span>
                            <span>
                              {trail.completed_length_miles.toFixed(1)}/
                              {trail.total_length_miles.toFixed(1)} miles
                            </span>
                            {trail.last_completed_at && (
                              <span>
                                {formatDistanceToNow(new Date(trail.last_completed_at), { addSuffix: true })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-emerald-600">
                            {trail.completion_percentage.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                      <Progress
                        value={trail.completion_percentage}
                        className="h-2 mb-3"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={(e) => handleSeeDetails(e, trail.trail_id)}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        See Details
                      </Button>
                    </div>
                  ))}

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleLoadMore}
                      >
                        Load More ({filteredTrails.length - displayCount} remaining)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
